import { format, parseISO, formatISO, setHours, setMinutes } from 'date-fns';
import { es, enUS } from 'date-fns/locale';

function createDateFromYYYYMMDD(yyyyMmDd) {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(yyyyMmDd)) {
    throw new Error('Parameter should have a yyyy-mm-dd format');
  }
  const isoString = `${yyyyMmDd}T12:00:00.000Z`;
  return new Date(isoString);
}

function isSpanishLanguage(userLanguage) {
  return userLanguage?.startsWith('es');
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function createDateWithTime(timeString) {
  const baseDate = new Date(2000, 0, 1);
  const [hours, minutes] = timeString.split(':').map(Number);
  return setMinutes(setHours(baseDate, hours), minutes || 0);
}

const formatSingleHour = (hour, isSpanish) => {
  if (!hour) return '';

  const timeDate = createDateWithTime(hour);

  if (isSpanish) {
    const minutes = timeDate.getMinutes();
    if (minutes === 0) {
      return format(timeDate, "H 'h'");
    }
    return format(timeDate, "HH:mm 'h'");
  }

  return format(timeDate, 'HH:mm');
};

export function formatScheduleHourPart(hour, userLanguage, isEndHour = false) {
  if (!hour) return '';

  const isSpanish = isSpanishLanguage(userLanguage);
  const timeDate = createDateWithTime(hour);

  if (isSpanish) {
    const hourNumber = timeDate.getHours();
    return isEndHour ? `${hourNumber} h` : `${hourNumber}`;
  }

  return format(timeDate, 'HH:mm');
}

export function formatHour(from, userLanguage, to = null) {
  if (!from) return '';

  const isSpanish = isSpanishLanguage(userLanguage);
  const fromFormatted = formatSingleHour(from, isSpanish);

  if (to) {
    const toFormatted = formatSingleHour(to, isSpanish);
    return `${fromFormatted} - ${toFormatted}`;
  }

  return fromFormatted;
}

export function formatDateWithNames(yyyyMmDd) {
  const date = createDateFromYYYYMMDD(yyyyMmDd);
  const formatted = format(date, 'cccc, MMMM d', { locale: es });
  return capitalize(formatted);
}

export function formatDateByLanguage(yyyyMmDd, userLanguage, hour = null) {
  const date = createDateFromYYYYMMDD(yyyyMmDd);
  const isSpanish = isSpanishLanguage(userLanguage);

  let dateString;

  if (isSpanish) {
    dateString = format(date, "cccc d 'de' MMMM", { locale: es });
    dateString = capitalize(dateString);
  } else {
    dateString = format(date, 'cccc MMMM d', { locale: enUS });
  }

  if (hour) {
    const formattedHour = formatHour(hour, userLanguage);
    dateString += `, ${formattedHour}`;
  }

  return dateString;
}

export function formatDateWithNumbers(yyyyMmDd) {
  const date = createDateFromYYYYMMDD(yyyyMmDd);
  return format(date, 'dd/MM/yyyy');
}

export function formatDateNumeric(date, userLanguage = 'es') {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const isSpanish = isSpanishLanguage(userLanguage);

  return format(dateObj, isSpanish ? 'dd/MM/yyyy' : 'MM/dd/yyyy', {
    locale: isSpanish ? es : enUS,
  });
}

export function toISOStringWithGMT(dateYYYYMMDD, hour = 0, gmtOffset = -3) {
  const date = parseISO(dateYYYYMMDD);
  date.setUTCHours(hour - gmtOffset, 0, 0, 0);

  return formatISO(date);
}

export function isAllDay(from, to) {
  return from === '00:00' && to === '23:59';
}
